import React, { useState, useMemo, lazy, Suspense, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Alert, Button } from '@c2fo/liquidity';
import { useAuth } from '../../hooks/useAuth';
import { useUserDetailsQuery } from './data/useUserDetailsQuery';
import { useUserDivisionsQuery } from './data/useUserDivisionsQuery';
import { useLoginHistoryPaginatedQuery } from './data/useLoginHistoryPaginatedQuery';
import { useUpdateUserMutation } from './data/useUpdateUserMutation';
import { useEmailSubscription } from './hooks/useEmailSubscription';
import { useUserActions } from './hooks/useUserActions';
import { UserHeader } from './components/UserHeader';
import { TabNavigation } from './components/TabNavigation';
import { UserDetailsTab } from './components/UserDetailsTab';
import { DivisionsTable } from './components/DivisionsTable';
import { OfferActivityTable } from './components/OfferActivityTable';
import { OfferSimulator } from '../OfferSimulator';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { Breadcrumb } from '../../components/Breadcrumb';
import { ErrorMessage, LoadingState } from './components/LoadingState';
import { handleClipboardCopy } from '../../utils/clipboard';
import type { Tab, User, OfferActivity, LoginHistory } from './types';

// Lazy load tab components for better initial load performance
const EmailSubscriptionTab = lazy(() =>
  import('./components/EmailSubscriptionTab').then((mod) => ({ default: mod.EmailSubscriptionTab })),
);
const DatacentersTab = lazy(() =>
  import('./components/DatacentersTab').then((mod) => ({ default: mod.DatacentersTab })),
);
const LoginHistoryTabPaginated = lazy(() =>
  import('./components/LoginHistoryTabPaginated').then((mod) => ({ default: mod.LoginHistoryTabPaginated })),
);

export const UserDetail: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { canImpersonate, canReconcileUsers } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('details');
  const [isCopied, setIsCopied] = useState(false);
  const [clipboardError, setClipboardError] = useState<string | null>(null);
  const [showCreatedAlert, setShowCreatedAlert] = useState(false);
  const [savedTempPassword, setSavedTempPassword] = useState<string | undefined>();
  const [savedCreatedEmail, setSavedCreatedEmail] = useState<string | undefined>();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Check if coming from create user page
  const navigationState = location.state as { fromCreate?: boolean; tempPassword?: string; email?: string } | null;
  const fromCreate = navigationState?.fromCreate ?? false;
  const tempPassword = savedTempPassword || navigationState?.tempPassword;
  const createdEmail = savedCreatedEmail || navigationState?.email;

  // Show created alert when coming from create page
  useEffect(() => {
    if (fromCreate && navigationState) {
      setShowCreatedAlert(true);
      // Save the values before clearing navigation state
      if (navigationState.tempPassword) {
        setSavedTempPassword(navigationState.tempPassword);
      }

      if (navigationState.email) {
        setSavedCreatedEmail(navigationState.email);
      }

      // Clear the navigation state to prevent showing alert on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [fromCreate, navigate, location.pathname, navigationState]);

  // React Query hooks for data fetching
  const { data: userDetailsData, isLoading: isLoadingUser, error: userError } = useUserDetailsQuery(uuid);

  const {
    data: divisions = [],
    isLoading: isLoadingDivisions,
    error: divisionsError,
    refetch: refreshDivisions,
  } = useUserDivisionsQuery(uuid);

  // Mutation for updating user
  const updateUserMutation = useUpdateUserMutation();

  const user = userDetailsData?.user ?? null;
  const datacenterRecords = userDetailsData?.datacenterRecords ?? [];

  // Pagination state for login history
  const [loginHistoryPage, setLoginHistoryPage] = useState(0);
  const {
    data: loginHistoryData,
    isLoading: isLoadingLoginHistory,
    hasNextPage,
    hasPreviousPage,
    totalPages,
  } = useLoginHistoryPaginatedQuery(uuid, loginHistoryPage);
  const loginHistory: LoginHistory[] = loginHistoryData?.data ?? [];
  const loginHistoryTotal = loginHistoryData?.total ?? 0;

  // TODO: Implement as React Query hook later
  const offerActivity: OfferActivity[] = [];

  // Memoize initialEmailSettings to prevent infinite re-renders
  const initialEmailSettings = useMemo(
    () => ({
      unsubscribe: false,
      education: false,
      frequency: 'weekly' as const,
    }),
    [],
  );

  const {
    settings: emailSettings,
    setSettings: setEmailSettings,
    saving: savingEmailSettings,
    save: saveEmailSettings,
  } = useEmailSubscription({
    userAuthUuid: uuid,
    initialSettings: initialEmailSettings,
  });

  // Use the new mutation for editUser
  const editUser = async (userData: Partial<User>) => {
    if (!uuid || !user) return;

    clearMessages();
    try {
      await updateUserMutation.mutateAsync({
        userAuthUuid: uuid,
        userData,
        currentUser: user,
      });
      setSuccessMessage('User updated successfully');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to update user';
      setErrorMessage(errorMsg);
      throw err; // Re-throw for modal handling
    }
  };

  const {
    impersonating,
    reconciling,
    sendingPasswordReset,
    resettingPassword,
    forgettingUser,
    successMessage: userActionSuccessMessage,
    errorMessage: userActionErrorMessage,
    clearMessages: clearUserActionMessages,
    impersonateUser,
    reconcileUser,
    sendPasswordResetEmail,
    resetPassword,
    forgetUser,
  } = useUserActions({
    authServiceUuid: uuid,
    user,
  });

  // Memoize computed values to prevent unnecessary re-renders (only when user exists)
  const userName = useMemo(() => (user ? `${user.firstName} ${user.lastName}` : ''), [user]);
  const userEmail = useMemo(() => user?.emailAddress ?? '', [user]);

  const fundingPartnerCount = useMemo(
    () => (divisions || []).filter((d) => d.isFundingPartnerMarket).length,
    [divisions],
  );
  const fundingPartnerTermsCount = useMemo(
    () => (divisions || []).filter((d) => d.hasAcceptedFundingPartnerTerms).length,
    [divisions],
  );

  const loading = isLoadingUser || isLoadingDivisions;
  const error = userError ?? divisionsError;

  const clearMessages = () => {
    setSuccessMessage(null);
    setErrorMessage(null);
    clearUserActionMessages();
  };

  if (loading) {
    return (
      <div className="relative p-6 mx-auto my-10">
        {/* Loading skeleton for header */}
        <div className="mb-6">
          <div className="h-8 w-64 bg-gray-300 rounded mb-2 animate-pulse" />
          <div className="h-4 w-48 bg-gray-300 rounded animate-pulse" />
        </div>

        {/* Loading skeleton for tabs */}
        <div className="flex space-x-4 mb-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-10 w-24 bg-gray-300 rounded animate-pulse" />
          ))}
        </div>

        {/* Loading skeleton for content */}
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 bg-gray-300 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  if (error || !user) {
    return (
      <div className="p-6">
        <ErrorMessage
          message={error?.message ?? 'User not found'}
          aria-label="Error loading user"
          onRetry={() => window.location.reload()}
        />
      </div>
    );
  }

  const handleCopyPassword = () => {
    if (tempPassword) {
      handleClipboardCopy(
        tempPassword,
        () => {
          setIsCopied(true);
          setClipboardError(null);
          setTimeout(() => setIsCopied(false), 2000);
        },
        () => {
          setClipboardError('Failed to copy password. Please copy it manually.');
        },
      );
    }
  };

  return (
    <ErrorBoundary>
      <div className="p-6 max-w-[1600px] mx-auto" role="main" aria-label="User details">
        <Breadcrumb
          items={[
            { label: 'Users', href: '/users' },
            { label: user ? `${user.firstName} ${user.lastName}` : 'User Details' },
          ]}
        />

        {showCreatedAlert && tempPassword && (
          <div className="mb-4">
            <Alert
              type="success"
              variant="filled"
              title="User Created Successfully"
              description={
                <div>
                  <div className="flex items-center justify-between">
                    <span>
                      <strong>{createdEmail || userEmail}</strong> was created with password{' '}
                      <strong className="font-mono">{tempPassword}</strong>
                    </span>
                    <Button variant="secondary" size="sm" onClick={handleCopyPassword} className="ml-4">
                      {isCopied ? 'Copied!' : 'Copy Password'}
                    </Button>
                  </div>
                  {clipboardError && <p className="mt-2 text-sm text-red-600">{clipboardError}</p>}
                </div>
              }
              full
              action={{
                onClick: () => setShowCreatedAlert(false),
                text: 'Dismiss',
              }}
            />
          </div>
        )}

        {showCreatedAlert && !tempPassword && (
          <div className="mb-4">
            <Alert
              type="success"
              variant="filled"
              description={`${createdEmail || userEmail} was created successfully.`}
              full
              action={{
                onClick: () => setShowCreatedAlert(false),
                text: 'Dismiss',
              }}
            />
          </div>
        )}

        {user.forgottenOn && (
          <div className="mb-4">
            <Alert
              type="error"
              variant="filled"
              title="User forgotten"
              description={`This user requested their account to be forgotten on ${new Date(user.forgottenOn).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })}. All personally identifiable information should be empty and the user should not receive emails from C2FO in the future.`}
              full
            />
          </div>
        )}
        {(successMessage || userActionSuccessMessage) && (
          <div className="mb-4">
            <Alert
              type="success"
              variant="filled"
              description={successMessage || userActionSuccessMessage}
              full
              action={{
                onClick: clearMessages,
                text: 'Dismiss',
              }}
            />
          </div>
        )}
        {(errorMessage || userActionErrorMessage) && (
          <div className="mb-4">
            <Alert
              type="error"
              variant="filled"
              description={errorMessage || userActionErrorMessage}
              full
              action={{
                onClick: clearMessages,
                text: 'Dismiss',
              }}
            />
          </div>
        )}

        <UserHeader user={user} />

        <TabNavigation
          activeTab={activeTab}
          onTabChange={setActiveTab}
          datacenterCount={datacenterRecords.length}
          loginHistoryTotal={loginHistoryTotal}
          canReconcileUsers={canReconcileUsers}
        />

        {activeTab === 'details' && (
          <>
            <UserDetailsTab
              user={user}
              fundingPartnerCount={fundingPartnerCount}
              fundingPartnerTermsCount={fundingPartnerTermsCount}
              loginHistory={loginHistory}
              userId={uuid}
              canImpersonate={canImpersonate}
              impersonating={impersonating}
              editing={updateUserMutation.isPending}
              sendingPasswordReset={sendingPasswordReset}
              resettingPassword={resettingPassword}
              forgettingUser={forgettingUser}
              onImpersonate={impersonateUser}
              onEditUser={editUser}
              onSendPasswordResetEmail={sendPasswordResetEmail}
              onResetPassword={resetPassword}
              onForgetUser={forgetUser}
            />
            <DivisionsTable
              divisions={divisions}
              userId={uuid || ''}
              userName={userName}
              userEmail={userEmail}
              user={user}
              onDivisionsChange={refreshDivisions}
            />
            <OfferActivityTable offerActivity={offerActivity} />
            <OfferSimulator divisions={divisions} user={user} />
          </>
        )}

        {activeTab === 'email' && (
          <Suspense fallback={<LoadingState message="Loading email subscription..." />}>
            <EmailSubscriptionTab
              settings={emailSettings}
              onSettingsChange={setEmailSettings}
              saving={savingEmailSettings}
              onSave={saveEmailSettings}
            />
          </Suspense>
        )}

        {activeTab === 'datacenters' && canReconcileUsers && (
          <Suspense fallback={<LoadingState message="Loading datacenters..." />}>
            <DatacentersTab
              user={user}
              datacenterRecords={datacenterRecords}
              reconciling={reconciling}
              canReconcileUsers={canReconcileUsers}
              onReconcile={reconcileUser}
            />
          </Suspense>
        )}

        {activeTab === 'logins' && (
          <Suspense fallback={<LoadingState message="Loading login history..." />}>
            {isLoadingLoginHistory ? (
              <LoadingState message="Loading login history..." />
            ) : (
              <LoginHistoryTabPaginated
                loginHistory={loginHistory}
                total={loginHistoryTotal}
                currentPage={loginHistoryPage}
                totalPages={totalPages}
                hasNextPage={hasNextPage}
                hasPreviousPage={hasPreviousPage}
                onPageChange={setLoginHistoryPage}
                pageSize={50}
              />
            )}
          </Suspense>
        )}
      </div>
    </ErrorBoundary>
  );
};
